import { formatLocaleDateTime } from '../../Framework/Constants/DateHelpers';
import React from 'react';
import { SendsHistoryEntryType, SendsItemTrackingLinkType } from '../../Api/StandaloneSendsEndpoints';
import MailItemThumbnail, { MailItemNoThumbnail, MailItemTopUpRequired } from '../Mailboxes/MailItemThumbnail';
import { useLayout } from '../../Framework/Providers/LayoutProvider';
import StackVertical from '../../Framework/Components/Wrappers/StackVertical';
import { FlexAlign, FlexGap, FlexJustify } from '../../Framework/Data/Enums/LayoutEnums';
import { MailItemSendsType } from '../../Types/Response/Mailboxes/Mailbox';
import { enGB } from 'date-fns/locale';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClipboard, faEnvelope, faLocationDot, faUser } from '@fortawesome/pro-solid-svg-icons';
import StackHorizontal from '../../Framework/Components/Wrappers/StackHorizontal';
import { faLocationCrosshairs } from '@fortawesome/free-solid-svg-icons/faLocationCrosshairs';
import { useToasts } from '../../Framework/Providers/ToastsProvider';

export const SendsItemTrackingLink = (props: SendsItemTrackingLinkType) => {
	const { addToast } = useToasts();

	if (props.postageName && props.postageName.indexOf('Royal Mail') > -1) {
		return (
			<div>
				<a
					href={`https://www.royalmail.com/track-your-item#/tracking-results/${props.trackingId}`}
					target={'_blank'}
					rel={'noreferrer'}
					className={'sends-tracking-link'}
				>
					<FontAwesomeIcon icon={faLocationCrosshairs} fixedWidth={true} />
					Track Item
				</a>
			</div>
		);
	}

	return (
		<div>
			<div
				className={'sends-tracking-link'}
				onClick={(e) => {
					e.preventDefault();
					addToast('Copied to Clipboard', <FontAwesomeIcon icon={faClipboard} />);
					navigator.clipboard.writeText(props.trackingId);
				}}
			>
				<FontAwesomeIcon icon={faClipboard} fixedWidth={true} />
				{props.trackingId}
			</div>
		</div>
	);
};

const SendsHistoryItem = (props: SendsHistoryEntryType) => {
	const { isMedium } = useLayout();

	if (!props.mailItem) {
		return null;
	}

	if (props.linkToSend) {
		return (
			<a
				href={`${process.env.REACT_APP_CLIENT_HOST}/Mail#/mail/${props.mailItem.trackingId}`}
				target={'_parent'}
				className={'item-row item-row-history'}
				style={{
					gridTemplateColumns: isMedium
						? props.hidePageDetails || props.hideUserDetails
							? '6em 1fr 1fr 1fr 8em'
							: `6em 1fr 1fr 1fr 1fr 8em`
						: 'var(--thumbnail-size) 1fr',
				}}
			>
				{isMedium ? <SendsHistoryItemContent {...props} /> : <SendsHistoryItemContentSmall {...props} />}
			</a>
		);
	}

	return (
		<div
			className={'item-row item-row-static item-row-history'}
			style={{
				gridTemplateColumns: isMedium
					? props.hidePageDetails || props.hideUserDetails
						? '6em 1fr 1fr 1fr 8em'
						: `6em 1fr 1fr 1fr 1fr 8em`
					: 'var(--thumbnail-size) 1fr',
			}}
		>
			{isMedium ? <SendsHistoryItemContent {...props} /> : <SendsHistoryItemContentSmall {...props} />}
		</div>
	);
};

const SendsHistoryItemContent = (props: SendsHistoryEntryType) => {
	return (
		<>
			<SendsHistoryItemThumbnail {...props.mailItem} />
			{!props.hidePageDetails && (
				<StackVertical gap={FlexGap.ExtraSmall}>
					<div>
						{props.title}
						<div className={'fade'}>{props.mailItem.trackingId}</div>
					</div>
				</StackVertical>
			)}

			{!props.hideUserDetails && <div>{props.user.name}</div>}

			<div>{props.address?.addressId === 0 ? props.address?.name : props.address?.formatted}</div>

			<div>
				{props.postageOption?.name}
				<br />
				{props.mailItem.outgoingTrackingId && (
					<SendsItemTrackingLink
						trackingId={props.mailItem.outgoingTrackingId}
						postageName={props.postageOption.name}
					/>
				)}
			</div>

			<div>{formatLocaleDateTime(props.dateCreated, enGB)}</div>
		</>
	);
};

const SendsHistoryItemContentSmall = (props: SendsHistoryEntryType) => {
	return (
		<>
			<SendsHistoryItemThumbnail {...props.mailItem} />
			<StackVertical gap={FlexGap.Small}>
				{!props.hidePageDetails && (
					<StackHorizontal justify={FlexJustify.Between} align={FlexAlign.Center}>
						<div>
							<h5 className={'m-0'}>{props.title}</h5>
							<span className={'fade'}>{props.mailItem.trackingId}</span>
						</div>
						<small className={'fade'}>{formatLocaleDateTime(props.dateCreated, enGB)}</small>
					</StackHorizontal>
				)}

				<StackVertical gap={FlexGap.ExtraSmall}>
					{!props.hideUserDetails && (
						<div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 'var(--xs-unit)' }}>
							<FontAwesomeIcon icon={faUser} fixedWidth={true} />
							<div>{props.user.name}</div>
						</div>
					)}

					<div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 'var(--xs-unit)' }}>
						<FontAwesomeIcon icon={faLocationDot} fixedWidth={true} />
						<div>{props.address?.formatted}</div>
					</div>

					<div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 'var(--xs-unit)' }}>
						<FontAwesomeIcon icon={faEnvelope} fixedWidth={true} />
						<div>{props.postageOption?.name}</div>
					</div>

					{props.mailItem.outgoingTrackingId && (
						<div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 'var(--xs-unit)' }}>
							<FontAwesomeIcon icon={faLocationCrosshairs} fixedWidth={true} style={{ opacity: 0 }} />

							<SendsItemTrackingLink
								trackingId={props.mailItem.outgoingTrackingId}
								postageName={props.postageOption.name}
							/>
						</div>
					)}
				</StackVertical>
			</StackVertical>
		</>
	);
};

const SendsHistoryItemThumbnail = (props: MailItemSendsType) => {
	return (
		<div>
			{props.files.map((file, index) => (
				<div key={`sendsHistoryEnvelopesThumb${index}`}>
					{file.thumbnails && file.thumbnails[0]?.linkToFile ? (
						<MailItemThumbnail {...file.thumbnails[0]} />
					) : index === 0 && props.files[0]?.topupRequired ? (
						<MailItemTopUpRequired />
					) : (
						<MailItemNoThumbnail />
					)}
				</div>
			))}

			{props.files.length === 0 && <MailItemNoThumbnail />}
		</div>
	);
};

export default SendsHistoryItem;
